import * as React from 'react';
import styled, { css } from 'styled-components';
import { parse, isSameDay, isFuture } from 'date-fns';
import LanguageContext from '@lib/contexts/languageContext';
import { Trans } from '@lingui/react';
import Button from '@components/buttons';
import Font from '@components/Font';
import { Box, Flex } from '@components/layout/Grid';
import { CardTitle, ParagraphNewCourseCard } from '@components/texts';
import InstructorModal from '../modals/InstructorModal';
import { ProgramCategory } from '@gql/generated';

const Card = styled.div<{ state?: string }>`
  border-radius: 5px;
  border: 1px solid #eeeeee;
  overflow: hidden;
  ${props =>
    props.state !== 'active' &&
    css`
      background-color: #eeeeee;
    `};
  @media (max-width: ${props => `${props.theme.viewports.tablet}px`}) {
    border-radius: 3px;
  }
`;

// const ProgressBar = styled.div`
//   height: 6px;
//   width: 140px;
//   border-radius: 2px;
//   background-color: #d8d8d8;
// `;

// const ProgressBarValue = styled.div`
//   width: ${props => props.progressValue}%;
//   height: 6px;
//   background-color: ${props => props.theme.colors.primary['blue-2']};
// `;

// const ProgressBarLabel = styled.div`
//   font-size: 13px;
//   font-weight: 600;
//   color: #242424;
// `;

export const State = styled.div<{ type?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  margin: 5px;
  display: inline-block;
  height: 22px;
  line-height: 22px;
  border-radius: 5px;
  background-color: ${props =>
    props.type === ProgramCategory['Courses']
      ? props.theme.colors.secondary['green-3']
      : props.theme.colors.primary['blue-2']};
  text-align: center;
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  padding-left: 10px;
  padding-right: 10px;
  text-transform: uppercase;
`;

const TopRightButton = styled.div`
  position: absolute;
  top: 30px;
  right: 30px;
  width: 244px;
`;

const Thumbnail = styled.div<{ path?: string }>`
  background-position: center;
  background-size: cover;
  background-image: ${props => (props.path ? `url(${props.path})` : null)};
  border-bottom-left-radius: 2px;
  border-top-left-radius: 2px;
  margin-left: -1px;
  margin-top: -1px;
  margin-bottom: -1px;
  position: relative;
  @media (max-width: ${props => `${props.theme.viewports.tablet}px`}) {
    margin-top: -1px;
    margin-left: -1px;
    margin-right: -1px;
    height: 0;
    padding-bottom: 56%;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    overflow: hidden;
  }
  &:before {
    content: '';
    display: block;
    padding-bottom: 65%;
  }
`;

const ThumbnailInner = styled.div`
  display: block;
  text-decoration: none;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const InstructorLabel = styled.a`
  display: inline-block;
  color: ${props => props.theme.colors.primary['blue-2']};
  text-decoration: none;
  font-weight: 600;
`;

const ThumbnailWrapper = props => {
  return (
    <RedirectTo path={props.path} locale={props.locale}>
      {props.children}
    </RedirectTo>
  );
};

const Metadata = props => (
  <Flex justifyContent="space-between" flexWrap={'wrap'}>
    {/* <Box>
      <Flex alignItems="center">
        {props.state !== 'expired' ? (
          <>
            <ProgressBar>
              <ProgressBarValue
                progressValue={props.progressValue ? props.progressValue : 0}
              />
            </ProgressBar>
            <Box ml="10px">
              <ProgressBarLabel>
                {props.progressValue}%{' '}
                <Trans id="metadata.completed" defaults={'Completed'} />
              </ProgressBarLabel>
            </Box>
          </>
        ) : null}
      </Flex>
    </Box> */}
    <Box>
      <ParagraphNewCourseCard italic>
        {props.expirationDate ? (
          <>
            {isSameDay(
              parse(props.expirationDate, `dd/MM/yyyy`, new Date()),
              new Date()
            ) ||
            isFuture(parse(props.expirationDate, `dd/MM/yyyy`, new Date())) ? (
              <Trans
                id="metadata.expire_on"
                message={'This course expires on {expirationDate}'}
                values={{ expirationDate: props.expirationDate }}
              />
            ) : (
              <Trans
                id="metadata.expired_on"
                message={'This course expired on {expirationDate}'}
                values={{ expirationDate: props.expirationDate }}
              />
            )}
          </>
        ) : null}
      </ParagraphNewCourseCard>
    </Box>
  </Flex>
);

const WhiteWrapper = styled.div`
  background-color: white;
  position: relative;
`;
const A = styled.a`
  text-decoration: none;
`;

const RedirectTo = props => {
  return <A href={props.path}>{props.children}</A>;
};

const TitleSection = props => {
  return props.path ? (
    <RedirectTo path={props.path} locale={props.locale}>
      <CardTitle>{props.contentTitle}</CardTitle>
    </RedirectTo>
  ) : (
    <CardTitle>{props.contentTitle}</CardTitle>
  );
};

const CourseCard = props => (
  <LanguageContext.Consumer>
    {locale => {
      return (
        <Flex
          className={props.className}
          as={Card}
          flexDirection={['column', 'column', 'row']}
          state={props.state}
          mb={10}
          css={props.css}
        >
          <Box
            flex={['0 auto', '0 auto', '0 407px']}
            as={Thumbnail}
            path={props.thumbnailUrl}
          >
            <ThumbnailWrapper path={props.path}>
              <ThumbnailInner>
                <State type={props.type}>
                  {props.type === ProgramCategory['Programs'] ? (
                    <Trans id="metadata.programLabel" message={'Program'} />
                  ) : null}

                  {props.type === ProgramCategory['Courses'] ? (
                    <Trans id="metadata.courseLabel" message={'Course'} />
                  ) : null}
                </State>
              </ThumbnailInner>
            </ThumbnailWrapper>
          </Box>
          <Box flex={1} px="30px" py="30px" as={WhiteWrapper}>
            <Flex
              flexDirection={'column'}
              justifyContent={'space-between'}
              style={{ height: '100%' }}
            >
              <Box mb="2px">
                {/* <H3NewCourseCard>{props.title}</H3NewCourseCard> */}
                <TitleSection
                  contentTitle={props.title}
                  path={props.path}
                  locale={locale}
                />
                {props.instructors && props.instructors.length > 0 ? (
                  <ParagraphNewCourseCard>
                    {props.type === ProgramCategory['Programs'] ? (
                      <Trans id="metadata.program_by" message={'Program by'} />
                    ) : null}
                    {props.type === ProgramCategory['Courses'] ? (
                      <Trans id="metadata.course_by" message={'Course by'} />
                    ) : null}
                    {props.instructors.map((instructor, index) => (
                      <InstructorModal key={index} slug={instructor.slug}>
                        {handleOpenModal => (
                          <InstructorLabel onClick={() => handleOpenModal()}>
                            {instructor.name}
                          </InstructorLabel>
                        )}
                      </InstructorModal>
                    ))}
                  </ParagraphNewCourseCard>
                ) : null}
              </Box>
              {props.state !== 'active' ? (
                <TopRightButton>
                  <Button appearance="outline">
                    <Font weight="600">Re-purchase Course with -20% off</Font>
                  </Button>
                </TopRightButton>
              ) : null}
              <Box mb="20px">
                <ParagraphNewCourseCard>
                  {props.description}
                </ParagraphNewCourseCard>
              </Box>
              <Box>
                <Metadata
                  progressValue={props.progressValue}
                  state={props.state}
                  expirationDate={props.expirationDate}
                  completionDate={props.completionDate}
                />
              </Box>
            </Flex>
          </Box>
        </Flex>
      );
    }}
  </LanguageContext.Consumer>
);

export default CourseCard;
